/* PreNumbers — Template Maker, reskinned (Linear-inspired dark).
   REPLACES: src/components/TemplateMaker/TemplateMaker.jsx

   Same component boundary, props, imports, and OUTPUT DATA SHAPES as your
   original — so ExportDataPage.mergeData() keeps working unchanged. Only the
   markup + classes change, plus a nicer metric browser:

     • The fragile cascading hover mega-menu is replaced by a persistent
       two-pane browser: pick a widget type, then browse categories → metrics
       with live search.
     • A staging tray shows what you've picked before you commit it (single
       pick for Card / Pie / Misc; a multi-select cart for the table & chart
       types — exactly the same single-vs-table logic as before).
     • Widgets land in a grid of COLLAPSIBLE cards (collapsed by default; click
       to expand and edit). Newly added widgets auto-expand.

   Props unchanged: { productName, dataToExport, entitySelected,
                       currentTemplate, setCurrentTemplate, templateSelected }
   `entitySelected` is accepted for signature parity (used by the host, not here).

   Deps: none new. We dropped the antd <Popconfirm> in favour of a small inline
   confirm popover (pure markup) and antd icons in favour of inline Lucide-style
   SVGs, so this file is dependency-free. Tokens come from
   styles/colors_and_type.css (already imported app-wide for the other reskins). */

import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Template_Options, Template_keys_description, Template_Main_Keys, productAnalysesKPIFields,
} from "../constants";
import "./TemplateMaker.css";

/* Deep copy so the dynamic Product Analysis population never mutates the import. */
const Local_Template_Options = JSON.parse(JSON.stringify(Template_Options));

/* ------------------------------ Icons (Lucide-style, 1.5px) ------------------------------ */
const svg = (p, size = 16) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{p}</svg>
);
const Ic = {
  search: (s = 15) => svg(<><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>, s),
  plus: (s = 15) => svg(<path d="M12 5v14M5 12h14" />, s),
  check: (s = 15) => svg(<path d="m20 6-11 11-5-5" />, s),
  x: (s = 15) => svg(<path d="M18 6 6 18M6 6l12 12" />, s),
  trash: (s = 15) => svg(<><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M10 11v6M14 11v6" /></>, s),
  folder: (s = 15) => svg(<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />, s),
  chevron: (s = 14) => svg(<path d="m6 9 6 6 6-6" />, s),
  card: (s = 16) => svg(<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M7 10h6M7 14h4" /></>, s),
  table: (s = 16) => svg(<><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 10h18M9 4v16" /></>, s),
  pie: (s = 16) => svg(<><path d="M12 3v9l7 4.5" /><circle cx="12" cy="12" r="9" /></>, s),
  chart: (s = 16) => svg(<><path d="M4 4v16h16" /><path d="m7 14 3-4 3 3 4-6" /></>, s),
  misc: (s = 16) => svg(<path d="m12 3 2.1 4.9L19 9l-3.6 3.3.9 5L12 14.8 7.7 17.3l.9-5L5 9l4.9-1.1z" />, s),
  layers: (s = 16) => svg(<><path d="m12 2 9 5-9 5-9-5z" /><path d="m3 12 9 5 9-5M3 17l9 5 9-5" /></>, s),
};

/* widget-type metadata: which output it produces, its colour + browse icon, and
   whether selecting a metric is single (Card/Pie/Misc) or multi (tables/charts). */
const TYPE_META = {
  "Big Number Card (KPIs)": { icon: Ic.card, tag: "Card", color: "var(--pn-brand-teal)", single: true },
  "Table (Firm Wide) KPIs": { icon: Ic.table, tag: "Table", color: "var(--pn-brand-yellow)", single: false },
  "Table (Projections)": { icon: Ic.table, tag: "Table", color: "var(--pn-brand-yellow)", single: false },
  "Product Analysis (KPIs)": { icon: Ic.layers, tag: "Per-product table", color: "#9a8cff", single: false },
  "Pie Chart": { icon: Ic.pie, tag: "Pie chart", color: "var(--pn-brand-coral)", single: true },
  "Table (Pricing)": { icon: Ic.table, tag: "Table", color: "var(--pn-brand-teal)", single: false },
  "Chart (Projections)": { icon: Ic.chart, tag: "Chart", color: "#6db7ff", single: false },
  "Miscellaneous": { icon: Ic.misc, tag: "Component", color: "var(--pn-brand-coral)", single: true },
};
const idOf = (subKey, name) => `${subKey}:::${name}`;

/* ------------------------------ data accessors (over the populated copy) ------------------------------ */
function getCategories(type) {
  return Local_Template_Options[type].keys;
}
function getMetrics(type, subKey) {
  if (!subKey) return [];
  const node = Local_Template_Options[type][subKey];
  if (!node || !node.keys) return [];
  return node.keys.map((k) => ({ subKey, name: node.items[k].name, metricInfo: node.items[k] }));
}

/* ============================ Template Maker ============================ */
function TemplateMaker({ productName, dataToExport, entitySelected, currentTemplate, setCurrentTemplate, templateSelected }) {
  /* Dynamically build the "Product Analysis (KPIs)" branch from the model's
     products — unchanged from your original implementation. */
  const populateTemplateOptions = useCallback(() => {
    if (Local_Template_Options["Product Analysis (KPIs)"].keys.length > 0) return; // already populated
    (dataToExport?.productAnalyses || []).forEach((p) => {
      Local_Template_Options["Product Analysis (KPIs)"].keys.push(p.name);
      const items_to_add = {};
      Object.keys(productAnalysesKPIFields.items).forEach((value) => {
        items_to_add[value] = {
          ...productAnalysesKPIFields.items[value],
          options: { keys: ["Product Name"], "Product Name": { value: p.name, name: "Product Name" } },
        };
      });
      Local_Template_Options["Product Analysis (KPIs)"][p.name] = {
        description: `Metrics related to the product analysis for ${p.name}`,
        keys: productAnalysesKPIFields.keys,
        items: items_to_add,
      };
    });
  }, [dataToExport]);

  useEffect(() => { populateTemplateOptions(); }, [populateTemplateOptions]);

  const [activeType, setActiveType] = useState("Big Number Card (KPIs)");
  const [activeCat, setActiveCat] = useState(getCategories("Big Number Card (KPIs)")[0]);
  const [query, setQuery] = useState("");
  const [stagedSingle, setStagedSingle] = useState(null);
  const [stagedMulti, setStagedMulti] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());
  const [pulse, setPulse] = useState(false);
  const [confirmIdx, setConfirmIdx] = useState(null);
  const [enterId, setEnterId] = useState(null);

  const meta = TYPE_META[activeType];
  const isSingle = meta.single;

  const selectType = (type) => {
    setActiveType(type);
    setActiveCat(getCategories(type)[0]);
    setStagedSingle(null); setStagedMulti([]); setQuery("");
  };

  const categories = getCategories(activeType);
  const metricsForActive = useMemo(() => getMetrics(activeType, activeCat), [activeType, activeCat]);
  const searchHits = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();
    const all = [];
    categories.forEach((c) => getMetrics(activeType, c).forEach((m) => {
      if (m.name.toLowerCase().includes(q) || (m.metricInfo.description || "").toLowerCase().includes(q)) all.push(m);
    }));
    return all;
  }, [query, activeType, categories]);
  const shown = searchHits || metricsForActive;

  const triggerPulse = () => { setPulse(true); setTimeout(() => setPulse(false), 600); };

  const onMetricClick = (m) => {
    if (isSingle) { setStagedSingle(m); triggerPulse(); return; }
    setStagedMulti((prev) => {
      const exists = prev.some((x) => idOf(x.subKey, x.name) === idOf(m.subKey, m.name));
      return exists ? prev.filter((x) => idOf(x.subKey, x.name) !== idOf(m.subKey, m.name)) : [...prev, m];
    });
    triggerPulse();
  };
  const isStaged = (m) => isSingle
    ? (stagedSingle && idOf(stagedSingle.subKey, stagedSingle.name) === idOf(m.subKey, m.name))
    : stagedMulti.some((x) => idOf(x.subKey, x.name) === idOf(m.subKey, m.name));

  /* ---- commit staged metric(s) — identical shapes to your original handlers ---- */
  const addStaged = () => {
    if (isSingle) {
      if (!stagedSingle) return;
      const mi = stagedSingle.metricInfo;
      const layout_id = crypto.randomUUID();
      const metric = { type: activeType, ...JSON.parse(JSON.stringify(mi)), user_given_name: mi.name, layout_id };
      setCurrentTemplate((prev) => ({ ...prev, metrics: [...prev.metrics, metric] }));
      setEnterId(layout_id); setExpanded((p) => new Set(p).add(layout_id)); setStagedSingle(null);
    } else {
      if (stagedMulti.length === 0) return;
      const layout_id = crypto.randomUUID();
      const name = activeType === "Table (Firm Wide) KPIs" ? "Firm Wide KPIs Table" : "Projections KPIs Table";
      const metric = {
        type: activeType, name, user_given_name: name, layout_id,
        data_type: "table", description: "A table containing firm wide KPIs",
        table_metrics: stagedMulti.map((it) => ({ ...JSON.parse(JSON.stringify(it.metricInfo)), subKey: it.subKey })),
      };
      setCurrentTemplate((prev) => ({ ...prev, metrics: [...prev.metrics, metric] }));
      setEnterId(layout_id); setExpanded((p) => new Set(p).add(layout_id)); setStagedMulti([]);
    }
    triggerPulse();
  };

  /* ---- card editing / deletion (same state transitions as before) ---- */
  const editMetric = (mIdx, field, value) =>
    setCurrentTemplate((prev) => { const m = [...prev.metrics]; m[mIdx] = { ...m[mIdx], [field]: value }; return { ...prev, metrics: m }; });
  const editOption = (mIdx, optKey, value) =>
    setCurrentTemplate((prev) => { const m = JSON.parse(JSON.stringify(prev.metrics)); m[mIdx].options[optKey].value = value; return { ...prev, metrics: m }; });
  const editSubOption = (mIdx, tIdx, optKey, value) =>
    setCurrentTemplate((prev) => { const m = JSON.parse(JSON.stringify(prev.metrics)); m[mIdx].table_metrics[tIdx].options[optKey].value = value; return { ...prev, metrics: m }; });
  const deleteMetric = (mIdx) => { setCurrentTemplate((prev) => ({ ...prev, metrics: prev.metrics.filter((_, i) => i !== mIdx) })); setConfirmIdx(null); };
  const deleteSub = (mIdx, tIdx) =>
    setCurrentTemplate((prev) => { const m = JSON.parse(JSON.stringify(prev.metrics)); m[mIdx].table_metrics = m[mIdx].table_metrics.filter((_, i) => i !== tIdx); return { ...prev, metrics: m }; });

  const toggleCard = (id) => setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const expandAll = () => setExpanded(new Set((currentTemplate.metrics || []).map((m) => m.layout_id)));
  const collapseAll = () => setExpanded(new Set());

  /* ============================ render ============================ */
  return (
    <div className="pn-tm">
      <div className="tm-head">
        <div className="tm-eyebrow"><span className="dot" />{productName} · Export data</div>
        <h1 className="tm-title">Template maker</h1>
        <div className="tm-sub">Compose the export your investors and board will see. Choose a widget, pick the metrics it shows, then save the template to reuse it.</div>
      </div>

      {!templateSelected ? (
        <div className="tm-empty">
          <div className="ring">{Ic.layers(24)}</div>
          <h3>Select a template to begin</h3>
          <p>Pick a template above, or create a new one, to start composing widgets.</p>
        </div>
      ) : (
        <>
          <div className="tm-field" style={{ maxWidth: 420, marginBottom: 22 }}>
            <label>Template name</label>
            <input className="tm-control title" value={currentTemplate.name}
              onChange={(e) => setCurrentTemplate({ ...currentTemplate, name: e.target.value })} placeholder="Enter template name" />
          </div>

          {/* ---------- builder ---------- */}
          <div className="tm-builder">
            <div className="tm-builder-head">
              <span className="step">1</span>
              <h2>Add a widget</h2>
              <span className="sub">— {(Template_keys_description[activeType] || {}).description}</span>
            </div>

            <div className="tm-typebar">
              {Template_Main_Keys.map((type) => {
                const tm = TYPE_META[type];
                return (
                  <button key={type} type="button" className={"tm-type" + (type === activeType ? " on" : "")} onClick={() => selectType(type)}>
                    <span className="tic">{tm.icon(16)}</span>
                    <span className="tlabel"><span className="tname">{type}</span><span className="ttag">{tm.tag}</span></span>
                  </button>
                );
              })}
            </div>

            <div className="tm-browser">
              <div className="tm-cats">
                <div className="tm-cat-label">{activeType === "Product Analysis (KPIs)" ? "Products" : "Categories"}</div>
                {categories.length === 0 && <div className="tm-cats-empty">No products in this model.</div>}
                {categories.map((c) => (
                  <div key={c} className={"tm-cat" + (c === activeCat ? " on" : "")} onClick={() => { setActiveCat(c); setQuery(""); }}>
                    <span className="cic">{Ic.folder(15)}</span>
                    <span className="cname">{c}</span>
                    <span className="ccount">{getMetrics(activeType, c).length}</span>
                  </div>
                ))}
              </div>

              <div className="tm-metrics-wrap">
                <div className="tm-search">
                  {Ic.search()}
                  <input placeholder={`Search ${activeType.toLowerCase()} metrics…`} value={query} onChange={(e) => setQuery(e.target.value)} />
                </div>
                <div className="tm-metrics">
                  {shown.length === 0 ? (
                    <div className="tm-metrics-empty">No metrics match “{query}”.</div>
                  ) : shown.map((m) => (
                    <div key={idOf(m.subKey, m.name)} className={"tm-metric" + (isStaged(m) ? " staged" : "")} onClick={() => onMetricClick(m)}>
                      <div className="mmain">
                        <div className="mtop">
                          <span className="mname">{m.name}</span>
                          <span className="tm-dt" data-dt={m.metricInfo.data_type}>{m.metricInfo.data_type}</span>
                          {searchHits && <span className="tm-dt subtle" data-dt="Number">{m.subKey}</span>}
                        </div>
                        <div className="mdesc">{m.metricInfo.description}</div>
                      </div>
                      <span className="madd">{isStaged(m) ? Ic.check(15) : Ic.plus(15)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={"tm-stage" + (pulse ? " pulse" : "")}>
              <span className="tm-stage-label">{isSingle ? "Selected" : "In this " + meta.tag.toLowerCase()}</span>
              <div className="tm-stage-chips">
                {isSingle ? (
                  stagedSingle ? (
                    <span className="tm-chip">{stagedSingle.name}<span className="sub">{stagedSingle.subKey}</span>
                      <span className="x" onClick={() => setStagedSingle(null)}>{Ic.x(13)}</span></span>
                  ) : <span className="tm-stage-placeholder">Pick a metric above to stage it.</span>
                ) : (
                  stagedMulti.length ? stagedMulti.map((m) => (
                    <span className="tm-chip" key={idOf(m.subKey, m.name)}>{m.name}
                      <span className="x" onClick={() => onMetricClick(m)}>{Ic.x(13)}</span></span>
                  )) : <span className="tm-stage-placeholder">Select one or more metrics to build this {meta.tag.toLowerCase()}.</span>
                )}
              </div>
              <button type="button" className="pn-btn pn-btn-accent" onClick={addStaged}
                disabled={isSingle ? !stagedSingle : stagedMulti.length === 0}>
                {Ic.plus()} Add {isSingle ? meta.tag.toLowerCase() : `${meta.tag.toLowerCase()} (${stagedMulti.length})`}
              </button>
            </div>
          </div>

          {/* ---------- widgets in the template ---------- */}
          <div className="tm-canvas-head">
            <h2>Widgets in this template</h2>
            <span className="count">· {currentTemplate.metrics.length}</span>
            <div className="sp" />
            {currentTemplate.metrics.length > 1 && (
              <div className="tm-expandtoggle">
                <button type="button" onClick={expandAll}>Expand all</button>
                <span className="div" />
                <button type="button" onClick={collapseAll}>Collapse all</button>
              </div>
            )}
          </div>

          {currentTemplate.metrics.length === 0 ? (
            <div className="tm-empty">
              <div className="ring">{Ic.card(24)}</div>
              <h3>No widgets yet</h3>
              <p>Add a widget above and it will appear here, ready to rename and fine-tune.</p>
            </div>
          ) : (
            <div className="tm-cards">
              {currentTemplate.metrics.map((metric, mIdx) => (
                <MetricCard key={metric.layout_id || mIdx} metric={metric} mIdx={mIdx}
                  enter={metric.layout_id === enterId}
                  open={expanded.has(metric.layout_id)} onToggle={() => toggleCard(metric.layout_id)}
                  confirmOpen={confirmIdx === mIdx} setConfirmIdx={setConfirmIdx}
                  onEdit={editMetric} onEditOption={editOption} onEditSubOption={editSubOption}
                  onDelete={deleteMetric} onDeleteSub={deleteSub} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ------------------------------ collapsible widget card ------------------------------ */
function MetricCard({ metric, mIdx, enter, open, onToggle, confirmOpen, setConfirmIdx, onEdit, onEditOption, onEditSubOption, onDelete, onDeleteSub }) {
  const meta = TYPE_META[metric.type] || {};
  const summary = metric.table_metrics
    ? `${meta.tag} · ${metric.table_metrics.length} metric${metric.table_metrics.length !== 1 ? "s" : ""}`
    : `${meta.tag}${metric.data_type && metric.data_type !== "table" ? " · " + metric.data_type : ""}`;
  return (
    <div className={"tm-card" + (enter ? " enter" : "") + (open ? " expanded" : "")}>
      <div className="tm-card-head" onClick={onToggle}>
        <span className="tm-card-dot" style={{ background: meta.color }} />
        <span className="tm-card-title">{metric.user_given_name || "Untitled widget"}</span>
        <span className="tm-card-summary">{summary}</span>
        <span className="tm-card-chev">{Ic.chevron(16)}</span>
        <span className="tm-card-del" title="Delete widget"
          onClick={(e) => { e.stopPropagation(); setConfirmIdx(confirmOpen ? null : mIdx); }}>{Ic.trash(15)}</span>
      </div>
      {confirmOpen && (
        <div className="tm-pop">
          <p>Delete this entire widget from the template?</p>
          <div className="row">
            <button type="button" className="pn-btn pn-btn-ghost pn-btn-sm" onClick={() => setConfirmIdx(null)}>Cancel</button>
            <button type="button" className="pn-btn pn-btn-danger pn-btn-sm" onClick={() => onDelete(mIdx)}>Delete</button>
          </div>
        </div>
      )}

      {open && (
        <div className="tm-card-body">
          <div className="tm-field">
            <label>Widget name</label>
            <input className="tm-control" value={metric.user_given_name || ""} onChange={(e) => onEdit(mIdx, "user_given_name", e.target.value)} />
          </div>
          <div className="tm-field">
            <label>Description</label>
            <input className="tm-control" value={metric.description || ""} onChange={(e) => onEdit(mIdx, "description", e.target.value)} />
          </div>

          {metric.options && metric.options.keys && metric.options.keys.map((optKey) => (
            <div className="tm-field" key={optKey}>
              <label>{metric.options[optKey].name}</label>
              <input className="tm-control" value={metric.options[optKey].value}
                onChange={(e) => onEditOption(mIdx, optKey, e.target.value)} placeholder={metric.options[optKey].name} />
            </div>
          ))}

          {metric.table_metrics && (
            <>
              <div className="tm-subhead">{metric.table_metrics.length} metric{metric.table_metrics.length !== 1 ? "s" : ""}</div>
              {metric.table_metrics.map((tm, tIdx) => (
                <div className="tm-sub" key={tIdx}>
                  <div className="tm-sub-top">
                    <span className="tm-sub-name">{tm.name}</span>
                    <span className="tm-dt" data-dt={tm.data_type}>{tm.data_type}</span>
                    <span className="tm-sub-del" title="Remove" onClick={() => onDeleteSub(mIdx, tIdx)}>{Ic.x(13)}</span>
                  </div>
                  {tm.options && tm.options.keys && tm.options.keys.map((optKey) => (
                    <div className="tm-sub-opt" key={optKey}>
                      <label>{tm.options[optKey].name}</label>
                      <input className="tm-control" value={tm.options[optKey].value}
                        onChange={(e) => onEditSubOption(mIdx, tIdx, optKey, e.target.value)} />
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default TemplateMaker;
